<template>
  <div class="photo-sphere-360-wrapper">
    <PhotoSphereViewer
      v-if="panoramaUrl"
      ref="photoSphereViewer"
      :key="image ? image.id : 'default'"
      :panoramaUrl="panoramaUrl"
      :markers="markers"
      :overlays="overlays"
      :initialView="initialView"
      @ready="onViewerReady"
      @loading="onViewerLoading"
      @error="onViewerError"
      @marker-select="onMarkerSelect"
      @click="onViewerClick"
    />
    <v-sheet
      v-else
      class="d-flex align-center justify-center h-100"
      color="transparent">
      <v-sheet class="d-flex flex-column align-center justify-center text-center">
        <v-icon size="64" color="grey lighten-1">mdi-panorama</v-icon>
        <p class="mt-4 grey--text">No panorama image available</p>
      </v-sheet>
    </v-sheet>
  </div>
</template>

<script>
import PhotoSphereViewer from './PhotoSphereViewer.vue';
import { pixelToYawPitch } from '../utils/panorama';
import { parseMaskPoints } from '../utils/findingsMetrics';
import { log, warn, error } from '@/utils/debugLogger';

// Equirectangular panorama size assumed when the image record carries no dimensions.
const DEFAULT_PANORAMA_WIDTH = 4057;
const DEFAULT_PANORAMA_HEIGHT = 2028;
const MIN_POLYGON_POINTS = 3;
const BADGE_MARKER_Z_INDEX = 30;
const POLYGON_STROKE_WIDTH = '3px';
const POLYGON_STROKE_WIDTH_ACTIVE = '5px';

export default {
  name: 'PhotoSphere360Viewer',
  components: {
    PhotoSphereViewer,
  },
  props: {
    image: {
      type: Object,
      default: null,
    },
    inspects: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      panoramaUrl: null,
      markers: [],
      overlays: [],
      initialView: {
        yaw: 0,
        pitch: 0,
        zoom: 50,
      },
      isLoading: false,
      persistentMarkers: [], // Store mask point markers (orange polygons)
      temporaryMarkers: [], // Store single anomaly markers (red polygons)
      reportMode: false, // AI Analysis report: the caller owns the whole marker list (see updateMarkers)
    };
  },
  watch: {
    image: {
      handler(newImage, oldImage) {
        if (!newImage) return;

        // Only the id/url identify the panorama. This watcher is deep and the prop is reassigned whenever the
        // host recomputes its inspects (AI Analysis does that on every row selection), so reloading on every
        // fire restarted the panorama for an image that had not changed: `loadPanorama` emits
        // `panorama-loading: true`, and with an unchanged source the viewer never re-emits ready, leaving the
        // loader up over a blank stage. Reload only when the panorama itself is different.
        const sameImage = oldImage && oldImage.id === newImage.id && oldImage.imageUrl === newImage.imageUrl;
        if (sameImage) return;

        // Clear markers when switching to a different image; the inspects watcher repopulates them.
        this.persistentMarkers = [];
        this.temporaryMarkers = [];
        this.markers = [];

        this.loadPanorama(newImage);
      },
      immediate: true,
      deep: true,
    },
    inspects: {
      handler(newInspects) {
        // An emptied list still reaches updateMarkers: in report mode it clears the markers (elsewhere it is a no-op).
        if ((newInspects && newInspects.length) || this.reportMode) {
          this.updateMarkers(newInspects || []);
        }
      },
      deep: true,
    },
  },
  methods: {
    loadPanorama(image) {
      if (!image) return;

      // Check if multiResConfig is available (Pannellum multires format)
      var v=false;
      if (image.multiResConfig && image.tilesBaseUrl&&v) {
        try {
          // Parse multiResConfig if it's a string
          const config = typeof image.multiResConfig === 'string'
            ? JSON.parse(image.multiResConfig)
            : image.multiResConfig;

          if (config.type === 'multires' && config.multiRes) {
            const multiRes = config.multiRes;

            // Calculate number of tile columns and rows
            // cubeResolution is the total width, tileResolution is the size of each tile
            const imageWidth = multiRes.cubeResolution || 8192;
            const imageHeight = imageWidth / 2; // Equirectangular ratio is 2:1
            const tileSize = multiRes.tileResolution || 512;

            // Number of tiles = total dimension / tile size
            const calculatedCols = Math.ceil(imageWidth / tileSize);
            const calculatedRows = Math.ceil(imageHeight / tileSize);

            // Photo Sphere Viewer requires cols and rows to be powers of 2
            // Round up to nearest power of 2
            const numCols = this.nextPowerOf2(calculatedCols);
            const numRows = this.nextPowerOf2(calculatedRows);

            // Configure for Photo Sphere Viewer's equirectangular adapter with tiles
            this.panoramaUrl = {
              width: imageWidth,
              cols: numCols,  // Number of tile columns (must be power of 2)
              rows: numRows,  // Number of tile rows (must be power of 2)
              tileSize: tileSize, // Size of each tile in pixels
              baseUrl: image.tilesBaseUrl,
              // Pannellum multires format: /{level}/{side}{y}_{x}.jpg
              // Photo Sphere Viewer format: function(col, row, zoom)
              tileUrl: (col, row, zoom) => {
                // Map Photo Sphere Viewer's zoom to Pannellum's level
                // zoom 0 = level 1, zoom 1 = level 2, etc.
                const level = zoom + 1;

                // For equirectangular, use 'f' as side (front face equivalent)
                // Pannellum format: /{level}/{side}{y}_{x}.jpg
                return `${image.tilesBaseUrl}/${level}/f${row}_${col}.${multiRes.extension || 'jpg'}`;
              },
            };
          } else {
            // Fallback to regular image
            this.panoramaUrl = image.lowResUrl || image.imageUrl;
          }
        } catch (err) {
          error('Error parsing multiResConfig:', err);
          // Fallback to regular image
          this.panoramaUrl = image.lowResUrl || image.imageUrl;
        }
      } else if (image.tilesGenerated && image.tilesBaseUrl&&v) {
        // Legacy tile format support
        const imageWidth = image.width || 4096;
        const imageHeight = image.height || (imageWidth / 2);
        const tileSize = image.tileResolution || 512;

        // Calculate number of tiles
        const calculatedCols = Math.ceil(imageWidth / tileSize);
        const calculatedRows = Math.ceil(imageHeight / tileSize);

        // Round up to nearest power of 2
        const numCols = this.nextPowerOf2(calculatedCols);
        const numRows = this.nextPowerOf2(calculatedRows);

        this.panoramaUrl = {
          width: imageWidth,
          cols: numCols,  // Number of tile columns (must be power of 2)
          rows: numRows,  // Number of tile rows (must be power of 2)
          tileSize: tileSize,
          baseUrl: image.tilesBaseUrl,
          tileUrl: (col, row, zoom) => {
            return `${image.tilesBaseUrl}/${zoom}/${col}/${row}.jpg`;
          },
        };
      } else {
        // For regular images (non-tiled)
        this.panoramaUrl = image.lowResUrl || image.imageUrl;
      }

      // Set initial view based on image properties
      this.initialView = {
        yaw: this.degreesToRadians(image.lockedYaw || 0),
        pitch: this.degreesToRadians(image.lockedPitch || 0),
        zoom: 50,
      };

      this.$emit('panorama-loading', true);
    },
    updateMarkers(inspects) {
      // Report mode (badge-numbered findings, AI Analysis): every update replaces the whole list and an empty list
      // clears everything, so a rejected area leaves the panorama instead of lingering as a persistent marker.
      const reportMode = Array.isArray(inspects) && inspects.some((i) => i && i.badgeIndex != null);
      if (reportMode) this.reportMode = true;
      if (!this.image || !inspects || !inspects.length) {
        // Clear temporary markers but keep persistent ones (unless the report owns the list)
        this.temporaryMarkers = [];
        if (this.reportMode) this.persistentMarkers = [];
        this.markers = [...this.persistentMarkers];
        return;
      }

      const imageWidth = this.image.width || DEFAULT_PANORAMA_WIDTH;
      const imageHeight = this.image.height || DEFAULT_PANORAMA_HEIGHT;

      // Determine if this is a coating analysis (multiple with mask points) or single anomaly
      const hasMultipleMaskPoints = reportMode || (inspects.length > 1 && inspects.some((i) => i.useMaskPoints));

      // Create polygon markers for each inspect
      const markerPairs = inspects.map((inspect, index) => {
        let polygonPoints = [];

        // Accepts the API's [{x,y}] JSON and the legacy ["x,y"] JSON; anything unparsable falls back to the bounding box.
        const maskPoints = inspect.useMaskPoints ? parseMaskPoints(inspect.maskPoints) : [];
        if (maskPoints.length >= MIN_POLYGON_POINTS) {
          polygonPoints = maskPoints.map(({ x, y }) => {
            const { yaw, pitch } = pixelToYawPitch(x, y, imageWidth, imageHeight);
            return [this.degreesToRadians(yaw), this.degreesToRadians(pitch)];
          });
        } else {
          if (inspect.useMaskPoints && inspect.maskPoints) {
            warn('maskPoints unparsable, falling back to the bounding box:', inspect.id, inspect.maskPoints);
          }
          polygonPoints = this.createBoundingBoxPolygon(inspect, imageWidth, imageHeight);
        }

        // Badge-numbered inspects (AI Analysis report) get a numbered badge on the polygon's top vertex;
        // everything else keeps the red centre dot at imageXCenterCoordinate / imageYCenterCoordinate.
        let centerMarker = null;
        if (inspect.badgeIndex != null && maskPoints.length >= MIN_POLYGON_POINTS) {
          const top = maskPoints.reduce((a, b) => (b.y < a.y ? b : a));
          const { yaw: badgeYaw, pitch: badgePitch } = pixelToYawPitch(top.x, top.y, imageWidth, imageHeight);
          centerMarker = {
            id: `badge-${index}-${inspect.id || Date.now()}`,
            html: `<span class="ps-badge${inspect.isActive ? ' ps-badge--active' : ''}">${inspect.badgeIndex}</span>`,
            anchor: 'bottom center',
            position: { yaw: this.degreesToRadians(badgeYaw), pitch: this.degreesToRadians(badgePitch) },
            zIndex: BADGE_MARKER_Z_INDEX,
            tooltip: { content: inspect.friendlyId || `Finding ${inspect.badgeIndex}`, position: 'top' },
            data: { type: 'badge', inspect, index },
          };
        } else if (inspect.imageXCenterCoordinate && inspect.imageYCenterCoordinate) {
          const centerX = parseInt(inspect.imageXCenterCoordinate);
          const centerY = parseInt(inspect.imageYCenterCoordinate);
          const { yaw: centerYaw, pitch: centerPitch } = pixelToYawPitch(
            centerX,
            centerY,
            imageWidth,
            imageHeight
          );

          centerMarker = {
            id: `center-${index}-${inspect.id || Date.now()}`,
            circle: 8,
            position: {
              yaw: this.degreesToRadians(centerYaw),
              pitch: this.degreesToRadians(centerPitch),
            },
            svgStyle: {
              fill: 'rgba(255, 0, 0, 0.9)',
              stroke: 'white',
              strokeWidth: '2px',
            },
            tooltip: {
              content: `Center: ${inspect.friendlyId || `Inspect ${index + 1}`}`,
              position: 'top',
            },
            zIndex: 20,
            data: {
              type: 'center',
              inspect,
              index,
            },
          };
        }

        const polygonMarker = {
          id: `inspect-${index}-${inspect.id || Date.now()}`,
          polygon: polygonPoints,
          // Per-inspect colours (fillColor / strokeColor / isActive) override the defaults: orange for mask
          // points, dashed blue for a bounding box.
          svgStyle: {
            fill: inspect.fillColor || (inspect.useMaskPoints ? 'rgba(255, 165, 0, 0.3)' : 'rgba(0, 123, 255, 0.2)'),
            stroke: inspect.strokeColor || (inspect.useMaskPoints ? 'orange' : '#007bff'),
            // The caller may override the width: the AI Analysis report draws thin contours (VIEWER_STROKE_WIDTH
            // in utils/reportFormat.js). Without an override the AI Inspect defaults stand.
            strokeWidth: inspect.strokeWidth || (inspect.isActive ? POLYGON_STROKE_WIDTH_ACTIVE : POLYGON_STROKE_WIDTH),
            strokeDasharray: inspect.useMaskPoints ? '0' : '10, 5', // Dashed border for single anomaly
          },
          tooltip: {
            content: inspect.friendlyId || `Inspect ${index + 1}`,
            position: 'top',
          },
          data: {
            inspect,
            index,
            isPersistent: inspect.useMaskPoints && hasMultipleMaskPoints,
          },
        };

        // Return array of markers (polygon + center if exists)
        return centerMarker ? [polygonMarker, centerMarker] : [polygonMarker];
      });

      // Flatten the marker pairs array
      const newMarkers = markerPairs.flat();

      // If this is a coating analysis with mask points, store as persistent
      if (hasMultipleMaskPoints) {
        this.persistentMarkers = newMarkers;
        this.temporaryMarkers = [];
        this.markers = [...this.persistentMarkers];
      } else {
        // Single anomaly - add as temporary marker on top of persistent ones
        this.temporaryMarkers = newMarkers;
        this.markers = [...this.persistentMarkers, ...this.temporaryMarkers];
      }

      // Also rotate to the first inspect.
      // Report mode (badge-numbered findings) owns navigation through focusInspect(): this auto-rotate runs in a
      // later $nextTick than the caller's focus() and would snap the camera back to finding #1 on every row click.
      if (!reportMode && inspects.length > 0 && inspects[0].centerPoint) {
        const centerPoint = inspects[0].centerPoint || {
          x: parseInt(inspects[0].imageXCenterCoordinate),
          y: parseInt(inspects[0].imageYCenterCoordinate),
        };

        const { yaw, pitch } = pixelToYawPitch(
          centerPoint.x,
          centerPoint.y,
          imageWidth,
          imageHeight,
        );

        // Rotate to the first inspect
        this.$nextTick(() => {
          if (this.$refs.photoSphereViewer) {
            this.$refs.photoSphereViewer.rotate({
              yaw: this.degreesToRadians(yaw),
              pitch: this.degreesToRadians(pitch),
            });
          }
        });
      }
    },
    nextPowerOf2(n) {
      // Find the next power of 2 greater than or equal to n
      // Max is 64 for Photo Sphere Viewer
      if (n <= 1) return 1;
      if (n <= 2) return 2;
      if (n <= 4) return 4;
      if (n <= 8) return 8;
      if (n <= 16) return 16;
      if (n <= 32) return 32;
      return 64; // Maximum allowed by Photo Sphere Viewer
    },
    createBoundingBoxPolygon(inspect, imageWidth, imageHeight) {
      const centerPoint = inspect.centerPoint || {
        x: parseInt(inspect.imageXCenterCoordinate),
        y: parseInt(inspect.imageYCenterCoordinate),
      };

      const imageW = inspect.imageWidth || 75;
      const imageH = inspect.imageHeight || 100;
      const padding = 0.2; // 20% padding
      const paddedWidth = imageW * (1 + 2 * padding);
      const paddedHeight = imageH * (1 + 2 * padding);
      const halfWidth = paddedWidth / 2;
      const halfHeight = paddedHeight / 2;

      // Calculate the four corners with padding
      const corners = [
        { x: centerPoint.x - halfWidth, y: centerPoint.y - halfHeight }, // Top-left
        { x: centerPoint.x + halfWidth, y: centerPoint.y - halfHeight }, // Top-right
        { x: centerPoint.x + halfWidth, y: centerPoint.y + halfHeight }, // Bottom-right
        { x: centerPoint.x - halfWidth, y: centerPoint.y + halfHeight }, // Bottom-left
      ];

      // Convert pixel coordinates to yaw/pitch
      return corners.map((corner) => {
        const { yaw, pitch } = pixelToYawPitch(corner.x, corner.y, imageWidth, imageHeight);
        return [this.degreesToRadians(yaw), this.degreesToRadians(pitch)];
      });
    },
    degreesToRadians(degrees) {
      return (degrees * Math.PI) / 180;
    },
    radiansToDegrees(radians) {
      return (radians * 180) / Math.PI;
    },
    onViewerReady(viewer) {
      this.isLoading = false;
      // Props can arrive before the underlying WebGL viewer mounts; the non-immediate
      // inspects watcher will not rebuild those initial markers on its own.
      this.updateMarkers(this.inspects);
      this.$emit('viewer-ready', viewer);
      this.$emit('panorama-loading', false);
    },
    onViewerLoading(loading) {
      this.isLoading = loading;
      this.$emit('panorama-loading', loading);
    },
    onViewerError(err) {
      error('Photo Sphere Viewer error:', err);
      this.$emit('viewer-error', err);
      this.$emit('panorama-loading', false);
    },
    onMarkerSelect(marker) {
      this.$emit('marker-select', marker);
      if (marker.data && marker.data.inspect) {
        this.$emit('inspect-select', marker.data.inspect);
      }
    },
    onViewerClick(event) {
      this.$emit('viewer-click', event);
    },
    // Public methods
    getViewer() {
      return this.$refs.photoSphereViewer?.viewer;
    },
    addMarker(marker) {
      if (this.$refs.photoSphereViewer) {
        this.$refs.photoSphereViewer.addMarker(marker);
      }
    },
    removeMarker(markerId) {
      if (this.$refs.photoSphereViewer) {
        this.$refs.photoSphereViewer.removeMarker(markerId);
      }
    },
    clearMarkers() {
      if (this.$refs.photoSphereViewer) {
        this.$refs.photoSphereViewer.clearMarkers();
      }
    },
    setPanorama(url) {
      if (this.$refs.photoSphereViewer) {
        this.$refs.photoSphereViewer.setPanorama(url);
      }
    },
    rotate(position) {
      if (this.$refs.photoSphereViewer) {
        this.$refs.photoSphereViewer.rotate(position);
      }
    },
    // Rotates the camera to one inspect's centre (report mode: row click → polygon).
    focusInspect(inspect) {
      if (!this.image || !inspect) return;
      const { yaw, pitch } = pixelToYawPitch(
        Number(inspect.imageXCenterCoordinate) || 0,
        Number(inspect.imageYCenterCoordinate) || 0,
        this.image.width || DEFAULT_PANORAMA_WIDTH,
        this.image.height || DEFAULT_PANORAMA_HEIGHT,
      );
      this.rotate({ yaw: this.degreesToRadians(yaw), pitch: this.degreesToRadians(pitch) });
    },
  },
};
</script>

<style scoped>
.photo-sphere-360-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  /* Markers are positioned against the viewer box; without this a marker whose projection falls outside the
     rendered panorama paints on the card background below the image. */
  overflow: hidden;
}
</style>

<!-- Unscoped on purpose: the markers plugin injects the badge HTML outside this component's scope. -->
<style>
.ps-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #c62828;
  color: #fff;
  font: 600 12px/22px 'IBM Plex Sans', sans-serif;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  border: 2px solid #fff;
}
.ps-badge--active {
  transform: scale(1.3);
  background: #03624c;
}
</style>
